/**
 * App-level design model: the five circular-coax fill families with
 * UI-friendly fields (degrees, unit-scaled dimensions, material presets).
 * Converted to the engine's SI-meter CoaxSpec by toCoaxSpec().
 */
import type { CoaxSpec } from "@kazam/xsec";

export interface DielProps {
  er: number;
  tand: number;
  /** Set when the values came from a materials preset. */
  presetId?: string;
}

export interface CondProps {
  sigma: number;
  muR: number;
  /** RMS surface roughness [um]. */
  roughnessUm: number;
  roughnessModel: "hammerstad" | "gradient";
  presetId?: string;
}

/** Dimensions are in the design's display unit (mm/um/mil). */
export interface HomogeneousFill {
  kind: "homogeneous";
  a: number;
  b: number;
  diel: DielProps;
}

export interface RadialLayer {
  /** Layer outer radius; the LAST layer's is forced to b. */
  rOuter: number;
  diel: DielProps;
}

export interface RadialFill {
  kind: "radial";
  a: number;
  b: number;
  /** Inside-out, >= 2 layers. */
  layers: RadialLayer[];
}

export interface WedgeSector {
  /** Angular span [deg]; the LAST sector's is forced to 360 - sum(others). */
  phiDeg: number;
  diel: DielProps;
}

export interface WedgeFill {
  kind: "wedge";
  a: number;
  b: number;
  /** CCW from theta = 0, >= 2 sectors. */
  sectors: WedgeSector[];
}

export interface ShellWedgeFill {
  kind: "shellWedge";
  a: number;
  /** Shell interface radius (a < c < b). */
  c: number;
  b: number;
  /** Wedge angle [deg] (0 < phi < 360). */
  phiDeg: number;
  /** e1 inner-wedge, e2 outer-wedge, e3 inner-rest, e4 outer-rest. */
  e1: DielProps;
  e2: DielProps;
  e3: DielProps;
  e4: DielProps;
}

export interface EccentricFill {
  kind: "eccentric";
  a: number;
  b: number;
  /** Inner-conductor offset (0 <= d < b - a). */
  d: number;
  diel: DielProps;
  /** Optional conformal two-layer at fraction t; diel is ignored when set. */
  twoLayer: { t: number; inner: DielProps; outer: DielProps } | null;
}

export type Fill = HomogeneousFill | RadialFill | WedgeFill | ShellWedgeFill | EccentricFill;
export type FillKind = Fill["kind"];

export type Unit = "mm" | "um" | "mil";
export const UNIT_TO_M: Record<Unit, number> = { mm: 1e-3, um: 1e-6, mil: 25.4e-6 };

export type Accuracy = "draft" | "normal" | "high";

export interface SweepSettings {
  f0: number;
  f1: number;
  n: number;
  log: boolean;
}

export interface Design {
  fill: Fill;
  unit: Unit;
  /** Applied to both conductors in milestone 1. */
  conductor: CondProps;
  sweep: SweepSettings;
  accuracy: Accuracy;
}

const DEG = Math.PI / 180;

/** The auto-balanced span of the last wedge sector [deg] (may be <= 0 when invalid). */
export function lastSectorDeg(fill: WedgeFill): number {
  return 360 - fill.sectors.slice(0, -1).reduce((s, x) => s + x.phiDeg, 0);
}

/** Convert a Design's fill to the engine CoaxSpec (SI metres, radians). */
export function toCoaxSpec(design: Design): CoaxSpec {
  const u = UNIT_TO_M[design.unit];
  const f = design.fill;
  switch (f.kind) {
    case "homogeneous":
      return { a: f.a * u, b: f.b * u, fill: { kind: "homogeneous", diel: { er: f.diel.er, tand: f.diel.tand } } };
    case "radial": {
      const layers = f.layers.map((l, i) => ({
        r: (i === f.layers.length - 1 ? f.b : l.rOuter) * u,
        er: l.diel.er,
        tand: l.diel.tand,
      }));
      return { a: f.a * u, b: f.b * u, fill: { kind: "radial", layers } };
    }
    case "wedge": {
      const sectors = f.sectors.map((s, i) => ({
        phi: (i === f.sectors.length - 1 ? lastSectorDeg(f) : s.phiDeg) * DEG,
        er: s.diel.er,
        tand: s.diel.tand,
      }));
      return { a: f.a * u, b: f.b * u, fill: { kind: "wedge", sectors } };
    }
    case "shellWedge":
      return {
        a: f.a * u,
        b: f.b * u,
        fill: {
          kind: "combined",
          rc: f.c * u,
          phi: f.phiDeg * DEG,
          eps: [f.e1.er, f.e2.er, f.e3.er, f.e4.er],
          tands: [f.e1.tand, f.e2.tand, f.e3.tand, f.e4.tand],
        },
      };
    case "eccentric":
      if (f.twoLayer) {
        return {
          a: f.a * u,
          b: f.b * u,
          d: f.d * u,
          fill: {
            kind: "eccentricRadial",
            t: f.twoLayer.t,
            er1: f.twoLayer.inner.er,
            er2: f.twoLayer.outer.er,
            tand1: f.twoLayer.inner.tand,
            tand2: f.twoLayer.outer.tand,
          },
        };
      }
      return { a: f.a * u, b: f.b * u, d: f.d * u, fill: { kind: "homogeneous", diel: { er: f.diel.er, tand: f.diel.tand } } };
  }
}
